import React, { useEffect, useMemo, useState } from 'react';
import {
  BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs,
  TextInput, fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import {
  EMPTY_TAG_FILTER, TagFilter, type TagFilterValue, UserTagEditor, tagCell, tagFilterParams, tagText,
} from './tags/shared';
import { AdminLink, type AdminTargetIn } from './adminNav';
import { useAdminAccess } from './adminAccess';

/**
 * User 360 — everything the platform knows about one account, on one page.
 *
 * Two controls sit above the sub-tabs and feed every read: the date range and
 * the picked user. Both live here so switching tabs never loses them, and every
 * handler is called with date_from/date_to so a number on one tab covers the
 * same window as a number on another. Each sub-tab is its own component and so
 * fetches only while it is on screen.
 *
 * The backend (admin_user360.py) is read-only — there are no POST handlers for
 * this section. The one write on the page is the picked user's tags, which go
 * through the tags section (Backend/user_tags.py), not this one.
 */

const TABS = ['Overview', 'Timeline', 'Chats', 'Messages', 'Searches',
  'Search performance', 'Saved & outreach', 'Sessions'];
/** A deep link's tab=: the label itself or its snake_case ("Saved & outreach" → saved_outreach). */
const tabKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const tabFromLink = (raw: string) => TABS.find(t => t === raw || tabKey(t) === tabKey(raw));

type Range = { from: string; to: string };
type TabProps = { userId: string; range: Range };

// ---------------------------------------------------------------------------
// Formatting / small atoms
// ---------------------------------------------------------------------------
const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86400000));

/** Only send the bounds that are set — an empty string means "unbounded". */
const rangeParams = (r: Range): Record<string, string> => {
  const p: Record<string, string> = {};
  if (r.from) p.date_from = r.from;
  if (r.to) p.date_to = r.to;
  return p;
};

const rangeLabel = (r: Range): string =>
  r.from && r.to ? `${r.from} → ${r.to}`
    : r.from ? `since ${r.from}`
      : r.to ? `up to ${r.to}`
        : 'all time';

const num = (v: any): string =>
  v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString();

const ms = (v: any): string =>
  v === null || v === undefined || v === '' ? '—' : `${Math.round(Number(v)).toLocaleString()} ms`;

const score = (v: any): string =>
  v === null || v === undefined || v === '' ? '—' : Number(v).toFixed(3);

const bytes = (v: any): string => {
  const n = Number(v) || 0;
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const isEmptyValue = (v: any): boolean =>
  v === null || v === undefined || v === ''
  || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

const labelize = (k: string) =>
  k.replace(/^_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const Pill: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'mute'; children: React.ReactNode }> =
  ({ tone, children }) => {
  const look = tone === 'ok' ? 'text-green-700 bg-green-50 border-green-200'
    : tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200'
      : tone === 'bad' ? 'text-red-600 bg-red-50 border-red-200'
        : 'text-grayscale-1200 bg-grayscale-200 border-grayscale-300';
  return <span className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${look}`}>{children}</span>;
};

const flagPill = (v: any, label: string) =>
  v === true ? <Pill tone="ok">{label}</Pill>
    : v === false ? <Pill tone="bad">not {label}</Pill>
      : <Pill tone="mute">{label}: unknown</Pill>;

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-sm text-grayscale-1100 py-6 text-center">{children}</div>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-grayscale-1100">{children}</p>
);

const Line: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-sm border-b border-grayscale-200 py-1 last:border-0">
    <span className="text-grayscale-1100 whitespace-nowrap">{label}</span>
    <span className="text-grayscale-1300 text-right break-words min-w-0">
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

/** Collapsible pretty-printed blob. Renders nothing when there is nothing to show. */
const Raw: React.FC<{ value: any; label?: string; maxHeight?: string }> =
  ({ value, label = 'raw', maxHeight = '18rem' }) => {
  const [open, setOpen] = useState(false);
  if (isEmptyValue(value)) return null;
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <div className="mt-1">
      <button onClick={() => setOpen(o => !o)}
        className="text-[11px] text-primary-700 underline font-grotesk">
        {open ? `Hide ${label}` : `Show ${label}`}
      </button>
      {open && (
        <pre className="mt-1 text-[11px] text-grayscale-1300 bg-grayscale-200 rounded-lg p-2 overflow-auto whitespace-pre-wrap break-words"
             style={{ maxHeight }}>{text}</pre>
      )}
    </div>
  );
};

const Drawer: React.FC<{
  title: React.ReactNode; subtitle?: React.ReactNode; onClose: () => void;
  wide?: boolean; children: React.ReactNode;
}> = ({ title, subtitle, onClose, wide, children }) => (
  <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
    <div className={`bg-grayscale-100 w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} h-full overflow-y-auto p-6 flex flex-col gap-4`}
         onClick={e => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk break-words">{title}</h2>
          {subtitle && <div className="text-xs text-grayscale-1100 break-words">{subtitle}</div>}
        </div>
        <Btn onClick={onClose}>Close</Btn>
      </div>
      {children}
    </div>
  </div>
);

/** Keeps ILIKE-heavy searches off every keystroke. */
function useDebounced(value: string, msDelay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), msDelay);
    return () => clearTimeout(t);
  }, [value, msDelay]);
  return v;
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Login export — the raw login history behind the numbers on this page
// ---------------------------------------------------------------------------
const csvDownload = (rows: any[], cols: string[], name: string) => {
  // A cell opening with = + - @ is executed as a formula by Excel and Sheets.
  // These values are names and organisations typed by users, so the leading
  // apostrophe makes the cell literal text.
  const esc = (s: any) => {
    const v = String(s ?? '');
    const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const csv = [cols.map(esc).join(',')]
    .concat(rows.map(r => cols.map(c => esc(r[c])).join(',')))
    .join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

// range_start anchors the weeks on the first day of the selected window, so
// the first bucket opens exactly where the range does instead of being a
// partial calendar week nobody asked for. It is the default because these
// exports are usually about a specific window rather than the calendar.
const WEEK_DAYS = ['range_start', 'monday', 'tuesday', 'wednesday', 'thursday',
  'friday', 'saturday', 'sunday'];
const WEEK_DAY_LABELS: Record<string, string> = {
  range_start: 'Start of the date range',
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};

/** The workbook arrives base64 inside the normal JSON envelope — the admin
 *  dispatcher wraps every response and cannot stream a file body — so it is
 *  turned back into bytes here rather than fetched from a bespoke route that
 *  would sit outside the panel's auth and audit logging. */
const b64Download = (b64: string, filename: string) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const GRAINS: [string, string, string][] = [
  ['week', 'Repeat logins by week',
   'One row per week: how many users who already existed when the week opened logged in during it.'],
  ['range', 'Overall for the date range',
   'One row for the whole window instead of weekly rows. A user active in several weeks counts once here, so it will not equal the weekly column added up.'],
  ['new_users', 'New signups and what they did next',
   'Everyone who signed up in the period, with a logged_in column saying which of them ever came back. The no-shows are kept — they are the answer to the question.'],
  ['user', 'Everything, one row per user',
   'Signup, first and last login, counts inside the window and lifetime, in one sheet. With a signup window set you can also keep the people who never logged in.'],
  ['event', 'One row per login',
   'The raw sign-in timestamps, UTC and IST, so you can do your own week arithmetic.'],
];

/**
 * The counts here come from user_sessions — one row per sign-in. The users
 * table has a last_login_at column but the live OTP path never writes it (it
 * is NULL for 2,305 of 2,306 accounts), and user_activity records no logins at
 * all, so this is the only real login history the platform keeps.
 */
const LoginExport: React.FC<{ range: Range }> = ({ range }) => {
  const [grain, setGrain] = useState('week');
  const [excl, setExcl] = useState(true);
  const [weekStart, setWeekStart] = useState('range_start');
  // Optional override of the page-level range. Empty means "follow the picker
  // above"; setting either end detaches this export from it, so a specific
  // window can be pulled without disturbing what the rest of the screen shows.
  const [ovFrom, setOvFrom] = useState('');
  const [ovTo, setOvTo] = useState('');
  const overridden = !!(ovFrom || ovTo);
  // A second, independent window: which PEOPLE are in scope, as opposed to
  // which sign-ins count. "What did the users who joined in July go on to do"
  // is unanswerable if the two share one range.
  const [sgFrom, setSgFrom] = useState('');
  const [sgTo, setSgTo] = useState('');
  const signupSet = !!(sgFrom || sgTo);
  const [onlyLoggedIn, setOnlyLoggedIn] = useState(true);
  const [wbBusy, setWbBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [preview, setPreview] = useState<any>(null);

  const params = () => {
    // All or nothing: mixing one override end with one picker end would give a
    // window that matches neither of the two things on screen.
    const dates = overridden
      ? { ...(ovFrom ? { date_from: ovFrom } : {}), ...(ovTo ? { date_to: ovTo } : {}) }
      : rangeParams(range);
    return {
      ...dates, grain, week_start_day: weekStart,
      ...(sgFrom ? { signup_from: sgFrom } : {}),
      ...(sgTo ? { signup_to: sgTo } : {}),
      only_logged_in: onlyLoggedIn ? '1' : '0',
      exclude_internal: excl ? '1' : '0', limit: '20000',
    };
  };

  const load = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelGet('user360', 'login_export', params());
    setBusy(false);
    if (error || !data) { setMsg({ kind: 'error', text: error || 'Export failed' }); return; }
    setPreview(data);
    setMsg({ kind: 'ok', text: `${data.total} row(s).` + (data.truncated ? ' Server cap hit — narrow the range.' : '') });
  };

  const save = () => {
    if (!preview) return;
    csvDownload(preview.rows || [], preview.columns || [], `logins_${grain}`);
  };

  // Every shape at once, on the windows currently set. Built server-side so
  // the sheets cannot disagree with each other: one set of parameters, one
  // pass, and an About sheet that says which windows produced the file.
  const workbook = async () => {
    setWbBusy(true); setMsg(null);
    const { data, error } = await panelGet('user360', 'login_workbook', params());
    setWbBusy(false);
    if (error || !data?.content_b64) {
      setMsg({ kind: 'error', text: error || 'Excel export failed' });
      return;
    }
    b64Download(data.content_b64, data.filename || 'login_history.xlsx');
    setMsg({
      kind: 'ok',
      text: `${data.filename} — ` +
        (data.sheets || []).map((x: any) => `${x.sheet} ${x.rows}`).join(', ') +
        ` (${Math.round((data.bytes || 0) / 1024)} KB).` +
        (data.truncated ? ' A sheet hit the row cap — narrow the window for the rest.' : ''),
    });
  };

  const meta = GRAINS.find(g => g[0] === grain);

  return (
    <Section title="Export login history"
      right={<span className="text-xs text-grayscale-1100">
        {overridden ? 'Using its own date range' : 'Uses the date range above'}
      </span>}>
      <div className="flex gap-2 items-end flex-wrap">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Shape
          <Select value={grain} onChange={v => { setGrain(v); setPreview(null); }}
            options={GRAINS.map(([v]) => v)}
            labels={Object.fromEntries(GRAINS.map(([v, l]) => [v, l]))} />
        </label>
        <label className="flex items-center gap-2 text-xs text-grayscale-1200 pb-2 cursor-pointer">
          <input type="checkbox" checked={excl}
            onChange={e => { setExcl(e.target.checked); setPreview(null); }} />
          Exclude our own accounts
        </label>
        {(grain === 'week' || grain === 'event') && (
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            Week starts on
            <Select value={weekStart}
              onChange={v => { setWeekStart(v); setPreview(null); }}
              options={WEEK_DAYS} labels={WEEK_DAY_LABELS} />
          </label>
        )}
        <Btn kind="primary" disabled={busy} onClick={load}>
          {busy ? 'Loading…' : 'Load'}
        </Btn>
        {preview && <Btn onClick={save}>Download CSV</Btn>}
        <Btn kind="primary" disabled={wbBusy} onClick={workbook}>
          {wbBusy ? 'Building…' : 'Download Excel — every sheet'}
        </Btn>
      </div>
      <Note>
        The Excel file carries all four shapes as separate sheets on the windows
        set below — users, every login, by week, and the range total — plus an
        About sheet recording which windows produced it. The CSV button exports
        only the shape shown above.
      </Note>

      <div className="flex gap-2 items-end flex-wrap border-t border-grayscale-300 pt-3">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Override from
          <TextInput type="date" value={ovFrom}
            onChange={v => { setOvFrom(v); setPreview(null); }} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Override to
          <TextInput type="date" value={ovTo}
            onChange={v => { setOvTo(v); setPreview(null); }} />
        </label>
        <Btn kind="primary" disabled={busy} onClick={load}>
          {busy ? 'Loading…' : 'Load this range'}
        </Btn>
        {overridden && (
          <Btn small onClick={() => { setOvFrom(''); setOvTo(''); setPreview(null); }}>
            Back to the page range
          </Btn>
        )}
        <div className="text-xs text-grayscale-1100 pb-2">
          {overridden
            ? `Logins counted: ${ovFrom || 'the beginning'} → ${ovTo || 'now'}`
            : 'Logins counted: following the date range above'}
        </div>
      </div>

      <div className="flex gap-2 items-end flex-wrap border-t border-grayscale-300 pt-3">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Signed up from
          <TextInput type="date" value={sgFrom}
            onChange={v => { setSgFrom(v); setPreview(null); }} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Signed up to
          <TextInput type="date" value={sgTo}
            onChange={v => { setSgTo(v); setPreview(null); }} />
        </label>
        <Btn kind="primary" disabled={busy} onClick={load}>
          {busy ? 'Loading…' : 'Load this signup cohort'}
        </Btn>
        {signupSet && (
          <Btn small onClick={() => { setSgFrom(''); setSgTo(''); setPreview(null); }}>
            Clear
          </Btn>
        )}
        <div className="text-xs text-grayscale-1100 pb-2">
          {signupSet
            ? `People in scope: signed up ${sgFrom || 'any time'} → ${sgTo || 'now'}`
            : 'People in scope: everyone'}
        </div>
      </div>

      {grain === 'user' && (
        <label className="text-xs text-grayscale-1200 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={onlyLoggedIn}
            onChange={e => { setOnlyLoggedIn(e.target.checked); setPreview(null); }} />
          Only users who logged in
          {!onlyLoggedIn && signupSet && (
            <span className="text-grayscale-1100">
              — signed-up-but-never-returned will show with a zero
            </span>
          )}
        </label>
      )}

      {meta && <Note>{meta[2]}</Note>}
      {weekStart === 'range_start' && grain !== 'range' && !overridden && !range.from && (
        <Note>
          Week buckets anchor on the first day of the window, and no start date is
          set — so they will fall back to calendar weeks starting Monday. Pick a
          From date above, or override it below.
        </Note>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      {excl && (
        <Note>
          Our own logins dominate this table — staff accounts are most of the
          sign-in history, so leaving them in roughly quadruples every weekly
          number. Untick only if you want the raw total.
        </Note>
      )}

      {preview && (
        <>
          <DataTable rows={(preview.rows || []).slice(0, 200)}
            columns={preview.columns || []} maxHeight="22rem" />
          {(preview.rows || []).length > 200 && (
            <div className="text-xs text-grayscale-1100">
              Showing the first 200 of {preview.total}. The CSV has all of them.
            </div>
          )}
          <Note>
            {preview.note}
            {preview.date_from || preview.date_to
              ? ` Logins: ${(preview.date_from || '').slice(0, 10) || 'the beginning'} to ${(preview.date_to || '').slice(0, 10) || 'now'}.`
              : ' Logins: all time.'}
            {preview.signup_from || preview.signup_to
              ? ` Signups: ${(preview.signup_from || '').slice(0, 10) || 'any time'} to ${(preview.signup_to || '').slice(0, 10) || 'now'}.`
              : ''}
          </Note>
        </>
      )}
    </Section>
  );
};

const PRESETS: [string, number | null][] = [
  ['Last 7 days', 7], ['Last 30 days', 30], ['Last 90 days', 90], ['All time', null],
];

/**
 * Deep link (owner, 2026-10-05): /admin?group=user360&email=…|user=<uuid>[&tab=timeline]
 * looks the account up with the finder's own call and picks the exact match, so
 * the page is the same as if it had been clicked in the finder.
 */
const User360Panel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState(TABS[0]);
  const [user, setUserState] = useState<any | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [linking, setLinking] = useState('');
  const [miss, setMiss] = useState<{ who: string; error: string } | null>(null);
  const setUser = (u: any | null) => { setMiss(null); setUserState(u); };

  useEffect(() => {
    if (!target) return;
    const t = tabFromLink(String(target.tab || '').trim());
    if (t) setTab(t);
    const want = String(target.email || target.user || '').trim();
    setLinking('');
    if (!want) return;
    let live = true;
    setMiss(null);
    setLinking(want);
    panelGet('user360', 'find', { q: want, limit: 25, page: 0, exclude_internal: '' })
      .then(({ data, error }) => {
        if (!live) return;
        setLinking('');
        const w = want.toLowerCase();
        const hit = (data?.rows || []).find((r: any) =>
          String(r.email || '').toLowerCase() === w || String(r.id || '').toLowerCase() === w);
        if (hit) { setUserState(decorateFind([hit])[0]); return; }
        setUserState(null);
        setMiss({ who: want, error: error || '' });
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);

  const range = useMemo<Range>(() => ({ from, to }), [from, to]);

  const applyPreset = (n: number | null) => {
    if (n === null) { setFrom(''); setTo(''); return; }
    setFrom(daysAgo(n - 1));
    setTo(iso(new Date()));
  };
  const presetActive = (n: number | null) =>
    n === null ? (!from && !to) : (from === daysAgo(n - 1) && to === iso(new Date()));

  return (
    <div className="flex flex-col gap-4">
      <Section title="Date range"
        right={<span className="text-xs text-grayscale-1100">Currently showing {rangeLabel(range)}</span>}>
        <div className="flex gap-2 items-end flex-wrap">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            From
            <TextInput type="date" value={from} onChange={setFrom} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            To
            <TextInput type="date" value={to} onChange={setTo} />
          </label>
          <div className="flex gap-1 flex-wrap pb-0.5">
            {PRESETS.map(([label, n]) => (
              <Btn key={label} small kind={presetActive(n) ? 'primary' : 'subtle'}
                onClick={() => applyPreset(n)}>{label}</Btn>
            ))}
          </div>
        </div>
        <Note>
          Every sub-tab below is fetched with this window, so the counts, the timeline, the chats
          and the search timings all cover the same period. The <b>To</b> date includes that whole
          day. Leave both empty for the account's entire history.
        </Note>
      </Section>

      <LoginExport range={range} />

      {linking && <Loading label={`Opening ${linking}…`} />}
      {miss && (
        <Msg kind="error">
          {miss.error ? `Could not look up ${miss.who}: ${miss.error}` : `No account for ${miss.who}.`}
          {miss.who.includes('@') && (
            <> <AdminLink to={{ group: 'email', tab: 'trace', email: miss.who }}>Trace this address</AdminLink></>
          )}
        </Msg>
      )}
      {user
        ? <PickedUser user={user} onClear={() => setUser(null)} />
        : <UserFinder onPick={setUser} />}

      <SubTabs tabs={TABS} active={tab} onChange={setTab} />

      {!user ? (
        <Section title={tab}>
          <Empty>Pick a user above to load this tab.</Empty>
        </Section>
      ) : (
        <>
          {tab === 'Overview' && <Overview userId={user.id} range={range} />}
          {tab === 'Timeline' && <Timeline userId={user.id} range={range} />}
          {tab === 'Chats' && <Chats userId={user.id} range={range} />}
          {tab === 'Messages' && <Messages userId={user.id} range={range} />}
          {tab === 'Searches' && <Searches userId={user.id} range={range} />}
          {tab === 'Search performance' && <SearchPerformance userId={user.id} range={range} />}
          {tab === 'Saved & outreach' && <SavedAndOutreach userId={user.id} range={range} />}
          {tab === 'Sessions' && <Sessions userId={user.id} range={range} />}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// User picker — footprint first, so two similar accounts are told apart before
// one is opened. An empty query lists the busiest accounts.
// ---------------------------------------------------------------------------
const FIND_COLS = ['email', 'lifecycle', 'name', 'organisation_name', 'designation', 'country',
  'billing_region', 'status', 'searches', 'chats', 'saves', 'logins',
  'last_login', 'created_at'];

/** A find row as the table shows it — and as the picker hands it on. */
const decorateFind = (list: any[]) => list.map((r: any) => ({
  ...r,
  status: [r.is_approved ? 'approved' : 'not approved',
    r.is_verified ? 'verified' : 'unverified',
    r.is_active ? 'active' : 'inactive'].join(' · '),
  tag_list: tagText(r),
}));

const UserFinder: React.FC<{ onPick: (u: any) => void }> = ({ onPick }) => {
  const [qRaw, setQRaw] = useState('');
  // Hides the team's own accounts, using the SAME list Analytics filters on.
  // Defaults to ON: staff accounts are the heaviest users on the platform, so
  // leaving them in puts them at the top of a table meant for finding customers.
  const [hideInternal, setHideInternal] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [exporting, setExporting] = useState(false);
  // Sorted on the server, because the list is paged — see DataTable's
  // serverSort. Empty means the default, busiest accounts first.
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  // Lifecycle / tag filter, applied by the server (the standard tag params).
  const [tf, setTf] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const tagParams = useMemo(() => tagFilterParams(tf), [tf]);
  const tagKey = JSON.stringify(tagParams);
  const q = useDebounced(qRaw);
  // A new search, filter or sort starts again from the first page.
  useEffect(() => { setPage(0); }, [q, hideInternal, perPage, sort, tagKey]);
  const find = useAdminGet('user360', 'find',
    { q, limit: perPage, page, exclude_internal: hideInternal ? '1' : '',
      sort: sort?.key || '', dir: sort?.dir || '', ...tagParams },
    [q, hideInternal, page, perPage, sort, tagKey]);

  const rows = useMemo(() => decorateFind(find.data?.rows || []), [find.data]);
  const total: number = find.data?.total ?? 0;
  const pages: number = find.data?.pages ?? 1;

  // "Download CSV" on the table exports the page. This exports the whole
  // match set, which is what "give me all of them" means — the reason the
  // list showed "50 of 50" and stopped was that nothing asked for the rest.
  const exportAll = async () => {
    setExporting(true);
    const { data, error } = await panelGet('user360', 'find',
      { q, limit: 5000, page: 0, exclude_internal: hideInternal ? '1' : '',
        sort: sort?.key || '', dir: sort?.dir || '', ...tagParams });
    setExporting(false);
    if (error || !data) { alert(error || 'The export could not be fetched.'); return; }
    const all = decorateFind(data.rows || []);
    // The button promised the whole set. If the server's per-request ceiling
    // cut it, say so rather than hand over a file that looks complete.
    if (all.length < (data.total ?? all.length)) {
      alert(`Only ${all.length.toLocaleString()} of ${Number(data.total).toLocaleString()} ` +
            'could be fetched in one go — narrow the search and export in parts.');
    }
    const cols = [...FIND_COLS, 'tag_list'];
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.map(esc).join(',')]
      .concat(all.map(r => cols.map(c => esc(r[c])).join(','))).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `user360_all_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Section title="Pick a user"
      right={
        <div className="flex items-center gap-3">
          <label className="text-xs text-grayscale-1100 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={hideInternal}
              onChange={e => setHideInternal(e.target.checked)} />
            Hide our own accounts
          </label>
          <span className="text-xs text-grayscale-1100">
            {total.toLocaleString()} match(es)
          </span>
          <Btn small onClick={exportAll} disabled={exporting || !total}>
            {exporting ? 'Exporting…' : `Download all ${total.toLocaleString()}`}
          </Btn>
        </div>
      }>
      <TextInput value={qRaw} onChange={setQRaw} wide
        placeholder="Search email, first / last name or organisation…" />
      <TagFilter value={tf} onChange={setTf} />
      <Note>
        Counts are lifetime totals, not filtered by the date range — they are here to identify the
        right account. Leave the box empty to see the busiest accounts first.
      </Note>
      {find.error && <Msg kind="error">{find.error}</Msg>}
      {find.loading ? <Loading /> : (
        <DataTable rows={rows} columns={FIND_COLS} csvName="user360_find"
          serverSort={sort}
          onSortChange={(key, dir) => setSort(key && dir ? { key, dir } : null)}
          unsortable={['lifecycle']}
          labels={{ lifecycle: 'Lifecycle · tags', organisation_name: 'Organisation', logins: 'Repeat Logins',
                    billing_region: 'Billing region', last_login: 'Last login',
                    created_at: 'Joined' }}
          empty={qRaw ? `No account matches “${qRaw}”.` : 'No users found.'}
          render={{
            lifecycle: tagCell,
            status: r => (
              <span className="flex gap-1">
                {flagPill(r.is_approved, 'approved')}
                {flagPill(r.is_verified, 'verified')}
                {flagPill(r.is_active, 'active')}
              </span>
            ),
          }}
          actions={r => <Btn small kind="primary" onClick={() => onPick(r)}>Select</Btn>} />
      )}
      {pages > 1 && (
        <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
          <span>
            Page {page + 1} of {pages} · showing {rows.length ? page * Number(perPage) + 1 : 0}–
            {page * Number(perPage) + rows.length} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Select value={perPage} onChange={setPerPage} options={['25', '50', '100', '250']}
              labels={{ '25': '25 / page', '50': '50 / page', '100': '100 / page', '250': '250 / page' }} />
            <Btn small onClick={() => setPage(0)} disabled={page === 0}>« First</Btn>
            <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</Btn>
            <Btn small onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>Next ›</Btn>
            <Btn small onClick={() => setPage(pages - 1)} disabled={page >= pages - 1}>Last »</Btn>
          </div>
        </div>
      )}
    </Section>
  );
};

const PickedUser: React.FC<{ user: any; onClear: () => void }> = ({ user, onClear }) => (
  <Section title="Selected user" right={
    <div className="flex items-center gap-3">
      {/* The change itself lives in Users → Directory's account drawer, which
          this deep link opens (with its warning and the change log). */}
      <AdminLink to={{ group: 'users', user: String(user.id) }}
        title="Open this account in Users → Directory to change its sign-in e-mail"
        className="text-xs text-red-700 underline underline-offset-2 whitespace-nowrap">
        Change e-mail…
      </AdminLink>
      <Btn small onClick={onClear}>Change user</Btn>
    </div>
  }>
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-base font-bold text-grayscale-1300 font-grotesk">{user.email}</span>
      <span className="text-sm text-grayscale-1200">{user.name || '—'}</span>
      <span className="text-xs text-grayscale-1100">
        {[user.organisation_name, user.designation, user.country].filter(Boolean).join(' · ') || 'no profile details'}
      </span>
    </div>
    <UserTagEditor userId={user.id} email={user.email} initial={user} />
    <div className="flex flex-wrap items-center gap-2">
      {flagPill(user.is_approved, 'approved')}
      {flagPill(user.is_verified, 'verified')}
      {flagPill(user.is_active, 'active')}
      <span className="text-xs text-grayscale-1100">
        lifetime: {num(user.searches)} searches · {num(user.chats)} chats · {num(user.saves)} saves
        {' '}· last login {fmtDate(user.last_login)}
      </span>
    </div>
    <div className="text-[11px] text-grayscale-1100">
      user_id <code>{user.id}</code>
    </div>
    <ViewAsControl user={user} />
  </Section>
);

/**
 * View as member (Backend/admin_view_as.py): opens the site in a new tab as
 * this member sees it — read-only by default (nothing is saved, nothing is
 * counted); write access is for super admins, with a reason, and still keeps
 * the stats untouched. Every view is listed below with who opened it.
 */
const ViewAsControl: React.FC<{ user: any }> = ({ user }) => {
  const { superAdmin } = useAdminAccess();
  const [mode, setMode] = useState<'read' | 'write'>('read');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [link, setLink] = useState('');
  const log = useAdminGet('user360', 'view_as_log', { user_id: String(user.id) }, [user.id]);
  const rows: any[] = Array.isArray(log.data?.rows) ? log.data.rows : [];

  const start = async () => {
    setBusy(true); setMsg(null); setLink('');
    // Opened now, inside the click, so a popup blocker lets it through; it is
    // pointed at the member's view once the server has made it.
    const w = window.open('about:blank', '_blank');
    const { data, error } = await panelPost('user360', 'view_as_start',
      { target_user_id: String(user.id), mode, reason: reason.trim() });
    setBusy(false);
    if (error || !data?.code) {
      w?.close();
      setMsg({ kind: 'error', text: error || 'Could not start the view' });
      return;
    }
    const url = `${window.location.origin}/#view_as=${encodeURIComponent(data.code)}`;
    if (w) {
      try { w.opener = null; } catch { /* ignore */ }
      w.location.href = url;
      setMsg({ kind: 'ok', text: `Opened in a new tab (${data.mode === 'write' ? 'write access' : 'read-only'}).` });
    } else {
      setLink(url);
      setMsg({ kind: 'ok', text: 'Your browser blocked the new tab — open this link within a minute:' });
    }
    setReason('');
    log.reload();
  };

  const endOne = async (id: string) => {
    const { error } = await panelPost('user360', 'view_as_end', { session_id: id, target_user_id: String(user.id) });
    if (error) setMsg({ kind: 'error', text: error });
    log.reload();
  };

  return (
    <details className="rounded-lg border border-grayscale-300 bg-white px-3 py-2 text-xs">
      <summary className="cursor-pointer text-grayscale-1300">
        <b>View as member</b> — see the site as {user.email} sees it
        {rows.some(r => r.live) && <span className="ml-2 text-red-700">· a view is open now</span>}
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        <p className="text-grayscale-1100">
          Opens a new tab signed in as this member, with their plan, settings and saved items. Read-only: nothing
          you do there is saved or counted in any stats, and their sign-in history is untouched. It ends after
          {' '}{log.data?.read_minutes ?? 30} minutes, or when you press End in that tab.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1">
            <input type="radio" checked={mode === 'read'} onChange={() => setMode('read')} /> Read-only
          </label>
          <label className={`flex items-center gap-1 ${superAdmin ? '' : 'opacity-50'}`}
            title={superAdmin ? '' : 'Super admins only'}>
            <input type="radio" checked={mode === 'write'} disabled={!superAdmin} onChange={() => setMode('write')} />
            With write access
          </label>
          {mode === 'write' && (
            <input value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
              placeholder="Why write access is needed (kept in the log)"
              className="min-w-[16rem] flex-1 rounded border border-grayscale-300 px-2 py-1" />
          )}
          <Btn kind="primary" small onClick={start}
            disabled={busy || (mode === 'write' && reason.trim().length < 5)}>
            {busy ? 'Opening…' : 'View as member'}
          </Btn>
        </div>
        {mode === 'write' && (
          <p className="text-red-700">
            Write access saves what you change to this member's account (profile, settings, saved items) for
            {' '}{log.data?.write_minutes ?? 15} minutes. Stats are still not touched.
          </p>
        )}
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {link && <a href={link} target="_blank" rel="noopener noreferrer" className="break-all text-primary-700 underline">{link}</a>}
        {log.error && <Msg kind="error">{log.error}</Msg>}
        {rows.length > 0 && (
          <DataTable rows={rows} tableId="user360_view_as_log" csvName={`view_as_${user.id}`} defaultSort="-created_at"
            maxHeight="16rem"
            columns={['created_at', 'admin_email', 'mode', 'reason', 'requests', 'refused', 'ended_at']}
            optionalColumns={['ip', 'claimed_at', 'last_seen_at', 'expires_at', 'ended_by']}
            labels={{ created_at: 'Started', admin_email: 'Admin', mode: 'Access', refused: 'Refused',
              ended_at: 'Ended', claimed_at: 'Opened', last_seen_at: 'Last request', expires_at: 'Expires',
              ended_by: 'Ended by', ip: 'IP' }}
            render={{
              created_at: r => fmtDate(r.created_at),
              mode: r => (r.mode === 'write' ? <span className="text-red-700">write</span> : 'read-only'),
              ended_at: r => (r.live ? <span className="text-green-700">open now</span>
                : r.ended_at ? fmtDate(r.ended_at) : (r.claimed_at ? 'timed out' : 'never opened')),
              claimed_at: r => (r.claimed_at ? fmtDate(r.claimed_at) : '—'),
              last_seen_at: r => (r.last_seen_at ? fmtDate(r.last_seen_at) : '—'),
              expires_at: r => fmtDate(r.expires_at),
            }}
            actions={r => (r.live ? <Btn small onClick={() => endOne(String(r.id))}>End</Btn> : null)}
            empty="" />
        )}
      </div>
    </details>
  );
};

// ---------------------------------------------------------------------------
// 1. Overview
// ---------------------------------------------------------------------------
const Overview: React.FC<TabProps> = ({ userId, range }) => {
  const rp = rangeParams(range);
  const sum = useAdminGet('user360', 'summary', { user_id: userId, ...rp },
    [userId, range.from, range.to]);
  const prof = useAdminGet('user360', 'profile', { user_id: userId }, [userId]);
  const [showAll, setShowAll] = useState(false);

  const counts = sum.data?.counts || {};
  const acc = prof.data?.account || {};
  const pd = prof.data?.profile || {};
  const interests: string[] = prof.data?.interests || [];

  const totalActivity = ['logins', 'searches', 'chats', 'messages', 'bookmarks', 'likes', 'contacts']
    .reduce((a, k) => a + (Number(counts[k]) || 0), 0);

  const fullRecord = useMemo(
    () => Object.entries(pd).filter(([, v]) => !isEmptyValue(v)),
    [pd]);

  return (
    <div className="flex flex-col gap-4">
      {sum.error && <Msg kind="error">{sum.error}</Msg>}
      {sum.loading ? <Loading /> : (
        <>
          <KPIRow>
            <KPI label="Logins" value={num(counts.logins)} />
            <KPI label="Searches" value={num(counts.searches)} />
            <KPI label="Chats" value={num(counts.chats)} />
            <KPI label="Messages" value={num(counts.messages)} />
            <KPI label="Bookmarks" value={num(counts.bookmarks)} />
            <KPI label="Likes" value={num(counts.likes)} />
            <KPI label="Contacts" value={num(counts.contacts)} />
          </KPIRow>
          <KPIRow>
            <KPI label="Instrumented searches" value={num(counts.instrumented_searches)}
              hint="have stage timings in Search performance" />
            <KPI label="Zero-result searches" value={num(counts.zero_result_searches)}
              hint={`${counts.zero_result_pct ?? 0}% of searches in range`} />
            <KPI label="First search" value={<span className="text-sm">{fmtDate(sum.data?.first_search)}</span>} />
            <KPI label="Last search" value={<span className="text-sm">{fmtDate(sum.data?.last_search)}</span>} />
          </KPIRow>
          {totalActivity === 0 && (
            <Empty>No recorded activity for this account in {rangeLabel(range)}. Widen the date range above.</Empty>
          )}
        </>
      )}

      <Section title="Who this is">
        {prof.error && <Msg kind="error">{prof.error}</Msg>}
        {prof.loading ? <Loading /> : (
          <>
            <div className="flex flex-wrap gap-2">
              {flagPill(acc.is_approved, 'approved')}
              {flagPill(acc.is_verified, 'verified')}
              {flagPill(acc.is_active, 'active')}
              {acc.has_password ? <Pill tone="mute">has password</Pill> : <Pill tone="mute">no password</Pill>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                <Line label="Name" value={[acc.first_name, acc.last_name].filter(Boolean).join(' ')} />
                <Line label="Email" value={acc.email} />
                <Line label="Organisation" value={pd.organisation_name} />
                <Line label="Designation" value={pd.designation} />
                <Line label="Location" value={[acc.city, acc.country].filter(Boolean).join(', ')} />
                <Line label="Phone" value={acc.phone || pd.phone} />
              </div>
              <div>
                <Line label="Signup source" value={acc.signup_source} />
                <Line label="Joined" value={fmtDate(acc.created_at)} />
                <Line label="Profile updated" value={fmtDate(acc.updated_at)} />
                <Line label="Last login" value={fmtDate(acc.last_login_at)} />
                <Line label="LinkedIn" value={pd.linkedin_profile_url
                  ? <a href={pd.linkedin_profile_url} target="_blank" rel="noreferrer"
                       className="text-primary-700 underline break-all">
                      {String(pd.linkedin_profile_url).replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  : null} />
                <Line label="user_id" value={<code className="text-xs break-all">{acc.id}</code>} />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-grayscale-1200 font-grotesk">
                Interests ({interests.length})
              </div>
              {interests.length ? (
                <div className="flex flex-wrap gap-1">
                  {interests.map(t => <Pill key={t} tone="mute">{t}</Pill>)}
                </div>
              ) : <span className="text-xs text-grayscale-1100">No interest tags saved.</span>}
            </div>
            {pd.professional_summary && (
              <div className="flex flex-col gap-1">
                <div className="text-xs font-bold text-grayscale-1200 font-grotesk">Professional summary</div>
                <p className="text-sm text-grayscale-1300 whitespace-pre-wrap">{pd.professional_summary}</p>
              </div>
            )}
          </>
        )}
      </Section>

      <Section title="Full profile record"
        right={<Btn small onClick={() => setShowAll(o => !o)}>
          {showAll ? 'Hide' : `Show ${fullRecord.length} field(s)`}
        </Btn>}>
        <Note>
          Every non-empty column of the user_profile_details row. Nulls and empty lists are hidden,
          so a field missing here was never filled in.
        </Note>
        {showAll && (
          fullRecord.length ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6">
              {fullRecord.map(([k, v]) => (
                <Line key={k} label={labelize(k)} value={
                  Array.isArray(v)
                    ? v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')
                    : typeof v === 'object'
                      ? <span className="text-xs break-all">{JSON.stringify(v)}</span>
                      : typeof v === 'boolean'
                        ? (v ? 'yes' : 'no')
                        : <span className="whitespace-pre-wrap">{String(v)}</span>
                } />
              ))}
            </div>
          ) : <Empty>This account has no profile record.</Empty>
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. Timeline
// ---------------------------------------------------------------------------
type Tone = 'ok' | 'warn' | 'bad' | 'mute';
const TONES: Tone[] = ['ok', 'warn', 'bad', 'mute'];
/** The colour a kind gets when the event itself does not say. billing and
 *  lifecycle events always carry their own `tone` (a payment is green, a
 *  refund amber, a deletion red), so these two are only the fallback. */
const KIND_TONE: Record<string, Tone> = {
  account: 'warn', login: 'mute', search: 'ok', chat: 'ok',
  message: 'mute', save: 'ok', contact: 'warn', feedback: 'warn',
  billing: 'ok', lifecycle: 'warn',
};
const eventTone = (e: any): Tone =>
  TONES.includes(e?.tone) ? e.tone : (KIND_TONE[e?.kind] || 'mute');
/** Mirrors admin_user360.TIMELINE_KINDS; the payload's `kinds` wins once it lands. */
const FALLBACK_KINDS = ['account', 'login', 'search', 'chat', 'message', 'save', 'contact', 'feedback',
  'billing', 'lifecycle'];

const Timeline: React.FC<TabProps> = ({ userId, range }) => {
  const [known, setKnown] = useState<string[]>(FALLBACK_KINDS);
  const [off, setOff] = useState<string[]>([]);
  const [limit, setLimit] = useState('100');

  const selected = known.filter(k => !off.includes(k));
  // All kinds on == send nothing, so a backend that grows a new kind still
  // returns it while the filter is untouched.
  const kindsParam = selected.length && selected.length < known.length ? selected.join(',') : '';

  const rp = rangeParams(range);
  const params: Record<string, string | number> = { user_id: userId, limit, ...rp };
  if (kindsParam) params.kinds = kindsParam;
  const tl = useAdminGet('user360', 'timeline', params,
    [userId, range.from, range.to, limit, kindsParam]);

  useEffect(() => {
    const k: string[] | undefined = tl.data?.kinds;
    if (k && k.length && k.join(',') !== known.join(',')) setKnown(k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tl.data]);

  const events: any[] = tl.data?.events || [];
  const byKind = tl.data?.by_kind || {};
  const barItems = Object.entries(byKind)
    .map(([label, value]) => ({ label, value: Number(value) || 0 }))
    .sort((a, b) => b.value - a.value);

  const toggle = (k: string) => setOff(o => {
    if (!o.includes(k)) {
      // Never let the filter empty out — an empty kinds list reads as "all"
      // server-side, which would silently undo the filter.
      if (selected.length <= 1) return o;
      return [...o, k];
    }
    return o.filter(x => x !== k);
  });

  return (
    <div className="flex flex-col gap-4">
      <Section title="Merged activity feed"
        right={<Btn small onClick={tl.reload}>Refresh</Btn>}>
        <div className="flex gap-2 items-center flex-wrap">
          {known.map(k => {
            const on = !off.includes(k);
            return (
              <button key={k} onClick={() => toggle(k)}
                title={on && selected.length <= 1 ? 'At least one kind has to stay on' : undefined}
                className={`text-xs rounded-full px-2.5 py-1 border font-grotesk whitespace-nowrap ${on
                  ? 'bg-primary-500 text-grayscale-100 border-primary-500 font-bold'
                  : 'border-grayscale-400 text-grayscale-1100 hover:bg-grayscale-200'}`}>
                {k}{byKind[k] !== undefined ? ` (${byKind[k]})` : ''}
              </button>
            );
          })}
          <span className="text-xs text-grayscale-1100">·</span>
          <Btn small onClick={() => setOff([])} disabled={!off.length}>All kinds</Btn>
          <Select value={limit} onChange={setLimit} options={['100', '500', '2000']}
            labels={{ '100': '100 events', '500': '500 events', '2000': '2000 events' }} />
        </div>
        {tl.error && <Msg kind="error">{tl.error}</Msg>}
        {tl.loading ? <Loading /> : (
          <>
            <div className="text-xs text-grayscale-1100">
              {tl.data?.returned ?? events.length} event(s) in {rangeLabel(range)}
              {tl.data?.truncated && tl.data?.note ? ` · ${tl.data.note}` : ''}
            </div>
            {barItems.length > 0 && <BarList items={barItems} />}
            {events.length === 0 ? (
              <Empty>No events of the selected kind(s) in {rangeLabel(range)}.</Empty>
            ) : (
              <div className="flex flex-col rounded-lg border border-grayscale-300 overflow-y-auto"
                   style={{ maxHeight: '44rem' }}>
                {events.map((e, i) => (
                  <div key={i} className="flex gap-3 items-start px-3 py-2 border-b border-grayscale-200 last:border-0">
                    <span className="text-[11px] text-grayscale-1100 whitespace-nowrap w-36 shrink-0 pt-0.5">
                      {fmtDate(e.when)}
                    </span>
                    <span className="w-20 shrink-0">
                      <Pill tone={eventTone(e)}>{e.kind}</Pill>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-grayscale-1300 font-medium">{e.label || e.kind}</div>
                      {e.detail && (
                        <div className="text-xs text-grayscale-1100 break-words whitespace-pre-wrap">{e.detail}</div>
                      )}
                      {(e.ip || e.ref_id || e.chat_id) && (
                        <div className="text-[11px] text-grayscale-1100 break-all">
                          {e.ip ? `IP ${e.ip}` : ''}
                          {e.ref_id ? `${e.ip ? ' · ' : ''}ref ${e.ref_id}` : ''}
                          {e.chat_id ? ` · chat ${e.chat_id}` : ''}
                        </div>
                      )}
                      <Raw value={e.raw} label="event JSON" maxHeight="14rem" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 3. Chats
// ---------------------------------------------------------------------------
const CHAT_COLS = ['created_at', 'title', 'messages', 'with_results', 'rating',
  'first_question', 'updated_at'];

const Chats: React.FC<TabProps> = ({ userId, range }) => {
  const [limit, setLimit] = useState('200');
  const [open, setOpen] = useState<string | null>(null);
  const rp = rangeParams(range);
  const chats = useAdminGet('user360', 'chats', { user_id: userId, limit, ...rp },
    [userId, range.from, range.to, limit]);

  const rows: any[] = chats.data?.rows || [];
  const total = chats.data?.total ?? rows.length;

  return (
    <div className="flex flex-col gap-4">
      <Section title={`Chats (${num(total)})`}
        right={
          <div className="flex gap-2 items-center">
            <Select value={limit} onChange={setLimit} options={['50', '200', '1000']}
              labels={{ '50': '50 rows', '200': '200 rows', '1000': '1000 rows' }} />
            <Btn small onClick={chats.reload}>Refresh</Btn>
          </div>
        }>
        {chats.error && <Msg kind="error">{chats.error}</Msg>}
        {chats.data?.truncated && (
          <Note>
            Showing the newest {rows.length} of {num(total)} chats in {rangeLabel(range)} — raise the
            row limit or narrow the date range to see further back.
          </Note>
        )}
        {chats.loading ? <Loading /> : (
          <DataTable rows={rows} columns={CHAT_COLS} searchable csvName="user360_chats"
            labels={{
              created_at: 'Started', updated_at: 'Last activity', messages: 'Msgs',
              with_results: 'With results', first_question: 'Opening question',
            }}
            empty={`No chats in ${rangeLabel(range)}.`}
            render={{
              rating: r => r.rating === null || r.rating === undefined
                ? <span className="text-grayscale-1100">—</span>
                : <Pill tone={Number(r.rating) >= 4 ? 'ok' : Number(r.rating) <= 2 ? 'bad' : 'warn'}>
                    {String(r.rating)}
                  </Pill>,
            }}
            actions={r => <Btn small onClick={() => setOpen(String(r.id))}>Open</Btn>} />
        )}
      </Section>
      {open && <ChatDrawer chatId={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const senderTone = (s: string) => (s === 'user' ? 'user' : s === 'system' ? 'system' : 'ai');

const Bubble: React.FC<{ m: any }> = ({ m }) => {
  const tone = senderTone(String(m.sender || ''));
  const look = tone === 'user'
    ? 'bg-primary-100 border-primary-300'
    : tone === 'system'
      ? 'bg-grayscale-200 border-grayscale-300'
      : 'bg-grayscale-100 border-grayscale-300';
  return (
    <div className={`flex ${tone === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`rounded-xl border px-3 py-2 max-w-[90%] flex flex-col gap-1 ${look}`}>
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-grayscale-1100">
          <span className="font-bold uppercase text-grayscale-1200">{m.sender || '?'}</span>
          <span>{fmtDate(m.when)}</span>
          <span>{num(m.chars)} chars</span>
          {m.results ? <Pill tone="ok">{num(m.results)} results</Pill> : null}
          {m.top_score !== null && m.top_score !== undefined
            ? <Pill tone="mute">top {score(m.top_score)}</Pill> : null}
          {m.avg_score !== null && m.avg_score !== undefined
            ? <Pill tone="mute">avg {score(m.avg_score)}</Pill> : null}
        </div>
        <div className="text-sm text-grayscale-1300 whitespace-pre-wrap break-words">{m.text || '—'}</div>
        {(m.top_titles || []).length > 0 && (
          <ul className="list-disc ml-4 text-[11px] text-grayscale-1100">
            {(m.top_titles || []).map((t: string, i: number) => <li key={i}>{t}</li>)}
          </ul>
        )}
        <Raw value={m.raw} label="message attachments" maxHeight="16rem" />
      </div>
    </div>
  );
};

const ChatDrawer: React.FC<{ chatId: string; onClose: () => void }> = ({ chatId, onClose }) => {
  const d = useAdminGet('user360', 'chat', { chat_id: chatId }, [chatId]);
  const chat = d.data?.chat || {};
  const messages: any[] = d.data?.messages || [];
  const feedback: any[] = d.data?.feedback || [];

  return (
    <Drawer wide onClose={onClose}
      title={chat.title || 'Chat'}
      subtitle={<>{chat.email || ''} · started {fmtDate(chat.created_at)} · chat <code>{chatId}</code></>}>
      {d.error && <Msg kind="error">{d.error}</Msg>}
      {d.loading ? <Loading /> : (
        <>
          {feedback.length > 0 && (
            <Section title="Feedback">
              {feedback.map((f, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Pill tone={Number(f.rating) >= 4 ? 'ok' : Number(f.rating) <= 2 ? 'bad' : 'warn'}>
                      rating {String(f.rating ?? '—')}
                    </Pill>
                    <span className="text-xs text-grayscale-1100">{fmtDate(f.created_at)}</span>
                  </div>
                  {f.feedback_text && (
                    <p className="text-sm text-grayscale-1300 whitespace-pre-wrap">{f.feedback_text}</p>
                  )}
                </div>
              ))}
            </Section>
          )}
          <Section title={`Conversation (${messages.length} message(s))`}>
            {messages.length === 0 ? <Empty>This chat has no messages.</Empty> : (
              <div className="flex flex-col gap-3">
                {messages.map((m, i) => <Bubble key={m.id || i} m={m} />)}
              </div>
            )}
          </Section>
        </>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// 4. Messages — the raw table
// ---------------------------------------------------------------------------
const MSG_COLS = ['when', 'chat_title', 'sender', 'text', 'chars', 'results', 'top_score',
  'avg_score', 'raw_bytes'];

const Messages: React.FC<TabProps> = ({ userId, range }) => {
  const [sender, setSender] = useState('All');
  const [withResults, setWithResults] = useState(false);
  const [qRaw, setQRaw] = useState('');
  const [limit, setLimit] = useState('100');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<any | null>(null);
  const [seenSenders, setSeenSenders] = useState<string[]>([]);
  const q = useDebounced(qRaw);

  const rp = rangeParams(range);
  // offset is sent only when it is non-zero: params cross the wire as strings,
  // and the handler's cap floors a truthy "0" to 1 — page one would silently
  // drop its newest message.
  const msgs = useAdminGet('user360', 'messages', {
    user_id: userId, limit, sender, q,
    with_results: withResults ? 'true' : '',
    ...(offset > 0 ? { offset } : {}), ...rp,
  }, [userId, range.from, range.to, limit, offset, sender, q, withResults]);

  // Any change to the filters invalidates the current page window.
  useEffect(() => { setOffset(0); },
    [userId, range.from, range.to, limit, sender, q, withResults]);

  const rows: any[] = msgs.data?.rows || [];
  const total = msgs.data?.total ?? 0;
  const lim = Number(msgs.data?.limit || limit) || 100;
  const off = Number(msgs.data?.offset ?? offset) || 0;
  const page = Math.floor(off / lim) + 1;
  const pages = Math.max(1, Math.ceil(total / lim));
  const pageBytes = rows.reduce((a, r) => a + (Number(r.raw_bytes) || 0), 0);

  // The handler matches `sender` exactly against the stored column but only
  // advertises user/ai/system, while most assistant rows are stored as
  // "assistant". Merging in the values actually seen keeps the filter usable.
  useEffect(() => {
    const fresh = rows.map(r => String(r.sender || '')).filter(Boolean);
    const extra = fresh.filter(s => !seenSenders.includes(s));
    if (extra.length) setSeenSenders(s => Array.from(new Set([...s, ...extra])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs.data]);

  const senderOptions = useMemo(() => {
    const declared: string[] = msgs.data?.senders || ['All', 'user', 'ai', 'system'];
    return Array.from(new Set([...declared, ...seenSenders]));
  }, [msgs.data, seenSenders]);

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Messages in range" value={num(total)} />
        <KPI label="On this page with results" value={num(msgs.data?.with_results)} />
        <KPI label="Avg result score" value={score(msgs.data?.avg_result_score)}
          hint="this page only" />
        <KPI label="Attachment payload" value={bytes(pageBytes)}
          hint="this page, fetched on demand" />
      </KPIRow>
      <Section title="Every message"
        right={<Btn small onClick={msgs.reload}>Refresh</Btn>}>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={sender} onChange={setSender} options={senderOptions} />
          <TextInput value={qRaw} onChange={setQRaw} wide placeholder="Text contains… (searches the server, not just this page)" />
          <label className="flex items-center gap-1.5 text-xs text-grayscale-1200 whitespace-nowrap">
            <input type="checkbox" checked={withResults}
              onChange={e => setWithResults(e.target.checked)} />
            Only messages carrying results
          </label>
          <Select value={limit} onChange={setLimit} options={['50', '100', '200', '500']}
            labels={{ '50': '50 / page', '100': '100 / page', '200': '200 / page', '500': '500 / page' }} />
        </div>
        <Note>
          {msgs.data?.score_note
            || 'Scores are the retriever’s per-result similarity stored on the message attachment.'}
        </Note>
        <Note>
          The result payload is <b>not</b> in this table — every attachment carries the full text of
          every result, which made a 200-row page ~10 MB. The table ships the size only; opening a
          row fetches that one message's attachments.
        </Note>
        <Note>
          The sender filter is an exact match on the stored value. The backend advertises
          <code> ai</code>, but assistant replies are written as <code>assistant</code> in most rows —
          if a filter comes back empty, try the other spelling (both appear in this list once seen).
        </Note>
        {msgs.error && <Msg kind="error">{msgs.error}</Msg>}
        {msgs.loading ? <Loading /> : (
          <DataTable rows={rows} columns={MSG_COLS} searchable csvName="user360_messages"
            maxHeight="34rem"
            labels={{
              when: 'When', chat_title: 'Chat', top_score: 'Top score', avg_score: 'Avg score',
              raw_bytes: 'Payload',
            }}
            empty={`No messages in ${rangeLabel(range)} for these filters.`}
            render={{
              top_score: r => score(r.top_score),
              avg_score: r => score(r.avg_score),
              text: r => <span className="block max-w-[26rem] truncate" title={r.text}>{r.text || '—'}</span>,
              raw_bytes: r => Number(r.raw_bytes) > 0
                ? bytes(r.raw_bytes)
                : <span className="text-grayscale-1100">none</span>,
            }}
            actions={r => (Number(r.raw_bytes) > 0
              ? <Btn small onClick={() => setOpen(r)}>Open ({bytes(r.raw_bytes)})</Btn>
              : <Btn small disabled title="This message stores no attachments — there is nothing to open.">Open</Btn>
            )} />
        )}
        <div className="flex items-center gap-2 justify-end text-xs text-grayscale-1100">
          <span>
            Showing {total ? off + 1 : 0}–{Math.min(off + lim, total)} of {num(total)}
          </span>
          <Btn small disabled={off <= 0} onClick={() => setOffset(Math.max(0, off - lim))}>Prev</Btn>
          Page {page} of {pages}
          <Btn small disabled={off + lim >= total} onClick={() => setOffset(off + lim)}>Next</Btn>
        </div>
        <Note>
          The row filter above the table narrows the current page only; the text box and the
          sender / results filters run on the server across the whole range.
        </Note>
      </Section>
      {open && <MessageDrawer row={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

/**
 * The flat messages table deliberately ships no attachment payload (each result
 * carries its full text — a 200-row page was ~10 MB), so the drawer fetches the
 * one message it is showing. The row it was opened from fills the header while
 * that request is in flight.
 */
const MessageDrawer: React.FC<{ row: any; onClose: () => void }> = ({ row, onClose }) => {
  const d = useAdminGet('user360', 'message', { message_id: row.id }, [row.id]);
  const loaded = d.data && Object.keys(d.data).length > 0 ? d.data : null;
  const m = loaded || row;
  const titles: string[] = m.top_titles || [];

  return (
    <Drawer wide onClose={onClose}
      title={`${m.sender || '?'} message`}
      subtitle={<>
        {m.chat_title || 'untitled chat'} · {fmtDate(m.when)} · chat <code>{m.chat_id}</code>
        {loaded?.user_email ? ` · ${loaded.user_email}` : ''}
      </>}>
      {d.error && <Msg kind="error">{d.error}</Msg>}
      <Section title="Facts">
        <Line label="Message id" value={<code className="text-xs break-all">{m.id}</code>} />
        <Line label="Characters" value={num(m.chars)} />
        <Line label="Results carried" value={num(m.results)} />
        <Line label="Top score" value={score(m.top_score)} />
        <Line label="Average score" value={score(m.avg_score)} />
        <Line label="Attachment payload" value={bytes(m.raw_bytes)} />
      </Section>
      <Section title="Text">
        <p className="text-sm text-grayscale-1300 whitespace-pre-wrap break-words">{m.text || '—'}</p>
      </Section>
      <Section title={`Top titles (${titles.length})`}>
        {titles.length ? (
          <ol className="list-decimal ml-5 text-sm text-grayscale-1300 flex flex-col gap-0.5">
            {titles.map((t: string, i: number) => <li key={i}>{t}</li>)}
          </ol>
        ) : <Empty>This message carried no results.</Empty>}
      </Section>
      <Section title="Raw attachment payload">
        {d.loading ? <Loading label={`Fetching ${bytes(row.raw_bytes)} of attachments…`} /> : (
          !loaded ? (
            <Empty>This message could not be loaded — it may have been deleted since the table was fetched.</Empty>
          ) : isEmptyValue(loaded.raw) || isEmptyValue(loaded.raw?.attachments) ? (
            <Empty>No attachments stored on this message.</Empty>
          ) : (
            <pre className="text-[11px] text-grayscale-1300 bg-grayscale-200 rounded-lg p-2 overflow-auto whitespace-pre-wrap break-words"
                 style={{ maxHeight: '40rem' }}>{JSON.stringify(loaded.raw, null, 2)}</pre>
          )
        )}
      </Section>
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// 5. Searches
// ---------------------------------------------------------------------------
const SEARCH_STATES = ['All', 'With results', 'No results'];
const SEARCH_COLS = ['created_at', 'query', 'filters_summary', 'backend', 'total'];

const Searches: React.FC<TabProps> = ({ userId, range }) => {
  const [state, setState] = useState('All');
  const [qRaw, setQRaw] = useState('');
  const [limit, setLimit] = useState('200');
  const q = useDebounced(qRaw);
  const rp = rangeParams(range);
  const s = useAdminGet('user360', 'searches',
    { user_id: userId, limit, state, q, ...rp },
    [userId, range.from, range.to, limit, state, q]);

  const rows: any[] = s.data?.rows || [];
  const byBackend: any[] = s.data?.by_backend || [];
  const topQueries: any[] = s.data?.top_queries || [];

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Searches in range" value={num(s.data?.total)} />
        <KPI label="Rows loaded" value={num(rows.length)}
          hint={s.data?.truncated ? 'capped by the row limit' : undefined} />
        <KPI label="Backends used" value={num(byBackend.length)} />
        <KPI label="Distinct queries" value={num(topQueries.length)} />
      </KPIRow>
      <Section title="Query history" right={<Btn small onClick={s.reload}>Refresh</Btn>}>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={state} onChange={setState} options={SEARCH_STATES} />
          <TextInput value={qRaw} onChange={setQRaw} wide placeholder="Query contains…" />
          <Select value={limit} onChange={setLimit} options={['50', '200', '1000', '2000']}
            labels={{ '50': '50 rows', '200': '200 rows', '1000': '1000 rows', '2000': '2000 rows' }} />
        </div>
        {s.error && <Msg kind="error">{s.error}</Msg>}
        {s.data?.truncated && (
          <Note>
            Showing the newest {rows.length} of {num(s.data?.total)} searches — raise the row limit
            or narrow the date range to see further back.
          </Note>
        )}
        {s.loading ? <Loading /> : (
          <DataTable rows={rows} columns={SEARCH_COLS} searchable csvName="user360_searches"
            labels={{ created_at: 'When', filters_summary: 'Filters', total: 'Results' }}
            empty={`No searches in ${rangeLabel(range)} for these filters.`}
            render={{
              total: r => Number(r.total) === 0
                ? <Pill tone="bad">0</Pill> : num(r.total),
              filters_summary: r => r.filters_summary
                ? <span className="block max-w-[20rem] truncate" title={r.filters_summary}>{r.filters_summary}</span>
                : <span className="text-grayscale-1100">none</span>,
            }} />
        )}
      </Section>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Section title="By backend">
          {byBackend.length ? (
            <>
              <BarList items={byBackend.map(b => ({ label: String(b.backend), value: Number(b.n) || 0 }))} />
              <DataTable rows={byBackend} columns={['backend', 'n', 'avg_results']}
                labels={{ n: 'Searches', avg_results: 'Avg results' }} maxHeight="14rem" />
            </>
          ) : <Empty>No searches in {rangeLabel(range)}.</Empty>}
        </Section>
        <Section title="Top queries">
          {topQueries.length ? (
            <>
              <BarList items={topQueries.slice(0, 10)
                .map(t => ({ label: String(t.query), value: Number(t.n) || 0 }))} />
              <DataTable rows={topQueries} columns={['query', 'n', 'best']} searchable
                csvName="user360_top_queries" maxHeight="18rem"
                labels={{ n: 'Times run', best: 'Best result count' }} />
            </>
          ) : <Empty>No searches in {rangeLabel(range)}.</Empty>}
        </Section>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 6. Search performance
// ---------------------------------------------------------------------------
const StatsBlock: React.FC<{ title: string; block: any; subtitle: string }> =
  ({ title, block, subtitle }) => {
  const searches = Number(block?.searches || 0);
  if (!block || !searches) {
    return (
      <Section title={title}>
        <Note>{subtitle}</Note>
        <Empty>No instrumented searches in this range.</Empty>
      </Section>
    );
  }
  const stages: any[] = block.stages || [];
  const retrieval = block.retrieval || {};
  const relevance = block.relevance || {};
  const funnel = [
    { label: 'Typesense returned', value: Number(retrieval.typesense_returned) || 0 },
    { label: 'Pinecone returned', value: Number(retrieval.pinecone_returned) || 0 },
    { label: 'Unique after dedupe', value: Number(retrieval.unique_after_dedupe) || 0 },
    { label: 'Found by both', value: Number(retrieval.found_by_both) || 0 },
  ];

  return (
    <Section title={title}>
      <Note>{subtitle}</Note>
      <KPIRow>
        <KPI label="Instrumented searches" value={num(searches)} />
        <KPI label="Errors" value={num(block.errors)} hint={`${block.error_rate ?? 0}% error rate`} />
        <KPI label="Avg results" value={num(retrieval.avg_results)} />
      </KPIRow>

      <div className="text-xs font-bold text-grayscale-1200 font-grotesk">Stage timings</div>
      {stages.length ? (
        <>
          <DataTable rows={stages} columns={['stage', 'avg_ms', 'p50', 'p95', 'max_ms', 'measured']}
            labels={{ avg_ms: 'Avg ms', p50: 'p50 ms', p95: 'p95 ms', max_ms: 'Max ms', measured: 'Rows' }}
            maxHeight="14rem" />
          <BarList items={stages.map(s => ({ label: String(s.stage), value: Number(s.avg_ms) || 0 }))} />
          <Note>Bars are average milliseconds per stage. Total is the wall clock, not the sum of the stages.</Note>
        </>
      ) : <Empty>No stage timings recorded.</Empty>}

      <div className="text-xs font-bold text-grayscale-1200 font-grotesk">Retrieval funnel</div>
      <BarList items={funnel} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <Line label="Typesense leg" value={ms(retrieval.typesense_ms)} />
        <Line label="Pinecone leg" value={ms(retrieval.pinecone_ms)} />
      </div>
      <Note>Funnel numbers are averages per search, not totals.</Note>

      <div className="text-xs font-bold text-grayscale-1200 font-grotesk">Relevance rerank</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <Line label="Triggered (avg per search)" value={num(relevance.triggered)} />
        <Line label="Reordered (avg per search)" value={num(relevance.reordered)} />
        <Line label="Min top score" value={score(relevance.min_top_score)} />
        <Line label="Avg top score" value={score(relevance.avg_top_score)} />
        <Line label="Max top score" value={score(relevance.max_top_score)} />
      </div>

      <div className="text-xs font-bold text-grayscale-1200 font-grotesk">By backend</div>
      <DataTable rows={block.by_backend || []} columns={['name', 'searches', 'avg_ms', 'avg_results']}
        labels={{ name: 'Backend', avg_ms: 'Avg ms', avg_results: 'Avg results' }}
        maxHeight="12rem" empty="No backend breakdown." />

      <div className="text-xs font-bold text-grayscale-1200 font-grotesk">By query-understanding model</div>
      <DataTable rows={block.by_ai_model || []} columns={['name', 'searches', 'avg_ms', 'used']}
        labels={{ name: 'Model', avg_ms: 'Avg ms', used: 'Times used' }}
        maxHeight="12rem" empty="No AI model breakdown." />

      <div className="text-xs font-bold text-grayscale-1200 font-grotesk">By relevance model</div>
      <DataTable rows={block.by_relevance_model || []}
        columns={['name', 'searches', 'avg_ms', 'avg_top_score', 'avg_scored', 'reordered']}
        labels={{
          name: 'Model', avg_ms: 'Avg ms', avg_top_score: 'Avg top score',
          avg_scored: 'Avg scored', reordered: 'Reordered',
        }}
        maxHeight="12rem" empty="No relevance model breakdown." />
    </Section>
  );
};

const LOG_COLS = ['created_at', 'query', 'backend', 'ai_model', 'ai_took_ms', 'retrieval_ms',
  'relevance_model', 'relevance_ms', 'relevance_max_rerank', 'total_ms', 'total_results',
  'typesense_returned', 'pinecone_returned', 'unique_after_dedupe', 'error'];

const SearchPerformance: React.FC<TabProps> = ({ userId, range }) => {
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [limit, setLimit] = useState('200');
  const [open, setOpen] = useState<string | null>(null);
  const rp = rangeParams(range);

  const stats = useAdminGet('user360', 'search_stats', { user_id: userId, ...rp },
    [userId, range.from, range.to]);
  const logs = useAdminGet('user360', 'search_logs',
    { user_id: userId, limit, errors_only: errorsOnly ? 'true' : '', ...rp },
    [userId, range.from, range.to, limit, errorsOnly]);

  const rows: any[] = logs.data?.rows || [];

  return (
    <div className="flex flex-col gap-4">
      {stats.error && <Msg kind="error">{stats.error}</Msg>}
      {stats.loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <StatsBlock title="This user" block={stats.data?.user}
              subtitle={`Instrumented searches by this account in ${rangeLabel(range)}.`} />
            <StatsBlock title="Whole platform" block={stats.data?.platform}
              subtitle={`Every account's instrumented searches in ${rangeLabel(range)} — the baseline a small per-user sample should be read against.`} />
          </div>
          {stats.data?.note && (
            <div className="text-xs rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
              {stats.data.note}
            </div>
          )}
        </>
      )}

      <Section title="Instrumented search log"
        right={
          <div className="flex gap-2 items-center flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-grayscale-1200 whitespace-nowrap">
              <input type="checkbox" checked={errorsOnly}
                onChange={e => setErrorsOnly(e.target.checked)} />
              Errors only
            </label>
            <Select value={limit} onChange={setLimit} options={['50', '200', '1000']}
              labels={{ '50': '50 rows', '200': '200 rows', '1000': '1000 rows' }} />
            <Btn small onClick={logs.reload}>Refresh</Btn>
          </div>
        }>
        {logs.error && <Msg kind="error">{logs.error}</Msg>}
        {logs.loading ? <Loading /> : (
          <DataTable rows={rows} columns={LOG_COLS} searchable csvName="user360_search_logs"
            maxHeight="30rem"
            labels={{
              created_at: 'When', ai_model: 'AI model', ai_took_ms: 'AI ms',
              retrieval_ms: 'Retrieval ms', relevance_model: 'Relevance model',
              relevance_ms: 'Relevance ms', relevance_max_rerank: 'Max rerank',
              total_ms: 'Total ms', total_results: 'Results',
              typesense_returned: 'Typesense', pinecone_returned: 'Pinecone',
              unique_after_dedupe: 'Unique',
            }}
            empty={errorsOnly
              ? `No failed searches in ${rangeLabel(range)}.`
              : `No instrumented searches in ${rangeLabel(range)} — this user's queries still show under Searches, just without stage timings.`}
            render={{
              error: r => r.error
                ? <span className="text-red-600 block max-w-[18rem] truncate" title={r.error}>{r.error}</span>
                : <span className="text-grayscale-1100">—</span>,
            }}
            actions={r => <Btn small onClick={() => setOpen(String(r.id))}>Open</Btn>} />
        )}
      </Section>
      {open && <SearchLogDrawer logId={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const SearchLogDrawer: React.FC<{ logId: string; onClose: () => void }> = ({ logId, onClose }) => {
  const d = useAdminGet('user360', 'search_detail', { log_id: logId }, [logId]);
  const r = d.data || {};
  const timings = r.timings || {};
  const breakdown = timings.retrieval_breakdown || {};

  return (
    <Drawer wide onClose={onClose}
      title={r.query || 'Search'}
      subtitle={<>{fmtDate(r.created_at)} · {r.backend || '—'} · log <code>{logId}</code></>}>
      {d.error && <Msg kind="error">{d.error}</Msg>}
      {d.loading ? <Loading /> : (
        <>
          {r.error && <Msg kind="error">{String(r.error)}</Msg>}
          <Section title="Outcome">
            <Line label="Total time" value={ms(r.total_ms)} />
            <Line label="Results returned" value={num(r.total_results)} />
            <Line label="Retrieval mode" value={r.retrieval_mode} />
            <Line label="Filters requested" value={isEmptyValue(r.filters_requested)
              ? null : <code className="text-xs break-all">{JSON.stringify(r.filters_requested)}</code>} />
          </Section>

          <Section title="AI understanding">
            <Line label="Used" value={r.ai_understanding_used ? <Pill tone="ok">yes</Pill> : <Pill tone="mute">no</Pill>} />
            <Line label="Model" value={r.ai_model} />
            <Line label="Took" value={ms(r.ai_took_ms)} />
            <Line label="Search query it produced" value={r.ai_search_query} />
            <Line label="Filters extracted" value={isEmptyValue(r.ai_filters_extracted)
              ? null : <code className="text-xs break-all">{JSON.stringify(r.ai_filters_extracted)}</code>} />
            <Line label="Pure filter query" value={r.ai_is_pure_filter ? 'yes' : 'no'} />
            <Line label="Dropped" value={isEmptyValue(r.ai_dropped)
              ? null : (Array.isArray(r.ai_dropped) ? r.ai_dropped.join(', ') : String(r.ai_dropped))} />
            <Line label="Reasoning" value={r.ai_reasoning
              ? <span className="whitespace-pre-wrap">{r.ai_reasoning}</span> : null} />
            <Raw value={r.ai_raw_response} label="ai_raw_response" />
          </Section>

          <Section title="Retrieval">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <Line label="Typesense returned" value={num(r.typesense_returned)} />
              <Line label="Pinecone returned" value={num(r.pinecone_returned)} />
              <Line label="Unique after dedupe" value={num(r.unique_after_dedupe)} />
              <Line label="Found by both" value={num(r.found_by_both)} />
              <Line label="Typesense leg" value={ms(r.typesense_ms)} />
              <Line label="Pinecone leg" value={ms(r.pinecone_ms)} />
              <Line label="Retrieval total" value={ms(r.retrieval_ms)} />
            </div>
          </Section>

          <Section title="Relevance decision">
            <Line label="Triggered" value={r.relevance_triggered
              ? <Pill tone="ok">yes</Pill> : <Pill tone="mute">no</Pill>} />
            <Line label="Reason" value={r.relevance_reason} />
            <Line label="Model" value={r.relevance_model} />
            <Line label="Max rerank score" value={score(r.relevance_max_rerank)} />
            <Line label="Results scored" value={num(r.relevance_scored)} />
            <Line label="Reordered" value={r.relevance_reordered ? 'yes' : 'no'} />
            <Line label="Took" value={ms(r.relevance_ms)} />
            <Raw value={r.relevance_raw} label="relevance_raw" />
          </Section>

          <Section title="Stage timings">
            {isEmptyValue(timings) ? <Empty>No timing breakdown stored for this search.</Empty> : (
              <>
                {Object.entries(timings)
                  .filter(([k, v]) => k !== 'retrieval_breakdown' && typeof v === 'number')
                  .map(([k, v]) => <Line key={k} label={labelize(k.replace(/_s$/, ''))} value={`${v}s`} />)}
                {Object.entries(breakdown)
                  .filter(([, v]) => typeof v === 'number')
                  .map(([k, v]) => (
                    <Line key={k} label={`Retrieval · ${labelize(k.replace(/_s$/, ''))}`} value={`${v}s`} />
                  ))}
                {breakdown.note && <Note>{String(breakdown.note)}</Note>}
              </>
            )}
          </Section>

          <Section title="Raw results">
            <Raw value={r.raw_results} label={`raw_results (${Array.isArray(r.raw_results) ? r.raw_results.length : 0} item(s))`}
              maxHeight="32rem" />
            {isEmptyValue(r.raw_results) && <Empty>No raw result payload stored.</Empty>}
          </Section>
        </>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// 7. Saved & outreach
// ---------------------------------------------------------------------------
const SAVED_COLS = ['when', 'title', 'university', 'asset_type', 'asset_id',
  'bookmarked', 'liked', 'contacted', 'chat_id'];
const OUTREACH_COLS = ['when', 'to', 'subject', 'status', 'technology', 'messages'];

const SavedAndOutreach: React.FC<TabProps> = ({ userId, range }) => {
  const [open, setOpen] = useState<any | null>(null);
  const rp = rangeParams(range);
  const saved = useAdminGet('user360', 'saved', { user_id: userId, limit: 500, ...rp },
    [userId, range.from, range.to]);
  const out = useAdminGet('user360', 'outreach', { user_id: userId, limit: 200, ...rp },
    [userId, range.from, range.to]);

  const savedRows: any[] = saved.data?.rows || [];
  const outRows: any[] = out.data?.rows || [];

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Saved assets" value={num(saved.data?.total ?? savedRows.length)} />
        <KPI label="Bookmarked" value={num(savedRows.filter(r => r.bookmarked).length)}
          hint="loaded rows" />
        <KPI label="Liked" value={num(savedRows.filter(r => r.liked).length)} hint="loaded rows" />
        <KPI label="Contacted" value={num(savedRows.filter(r => r.contacted).length)} hint="loaded rows" />
        <KPI label="Outreach threads" value={num(out.data?.total ?? outRows.length)} />
      </KPIRow>
      <Section title="Saved assets" right={<Btn small onClick={saved.reload}>Refresh</Btn>}>
        {saved.error && <Msg kind="error">{saved.error}</Msg>}
        {saved.data?.truncated && (
          <Note>
            Showing the newest {savedRows.length} of {num(saved.data?.total)} saved assets in
            {' '}{rangeLabel(range)} — narrow the date range to see further back.
          </Note>
        )}
        {saved.loading ? <Loading /> : (
          <DataTable rows={savedRows} columns={SAVED_COLS} searchable csvName="user360_saved"
            labels={{ when: 'When', asset_type: 'Type', asset_id: 'Asset id', chat_id: 'From chat' }}
            empty={`Nothing saved in ${rangeLabel(range)}.`}
            render={{
              bookmarked: r => r.bookmarked ? <Pill tone="ok">saved</Pill> : <span className="text-grayscale-1100">—</span>,
              liked: r => r.liked ? <Pill tone="ok">liked</Pill> : <span className="text-grayscale-1100">—</span>,
              contacted: r => r.contacted ? <Pill tone="warn">contacted</Pill> : <span className="text-grayscale-1100">—</span>,
            }} />
        )}
      </Section>
      <Section title="Outreach" right={<Btn small onClick={out.reload}>Refresh</Btn>}>
        {out.error && <Msg kind="error">{out.error}</Msg>}
        {out.loading ? <Loading /> : (
          <DataTable rows={outRows} columns={OUTREACH_COLS} searchable csvName="user360_outreach"
            labels={{ when: 'When', to: 'Sent to', messages: 'Msgs' }}
            empty={`No outreach in ${rangeLabel(range)}.`}
            actions={r => <Btn small onClick={() => setOpen(r)}>Open thread</Btn>} />
        )}
      </Section>
      {open && <OutreachDrawer row={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const OutreachDrawer: React.FC<{ row: any; onClose: () => void }> = ({ row, onClose }) => {
  const d = useAdminGet('user360', 'outreach_messages',
    { conversation_id: row.conversation_id }, [row.conversation_id]);
  const messages: any[] = d.data || [];

  return (
    <Drawer wide onClose={onClose}
      title={row.subject || 'Outreach thread'}
      subtitle={<>to {row.to} · {row.status || '—'} · started {fmtDate(row.when)}</>}>
      {d.error && <Msg kind="error">{d.error}</Msg>}
      {d.loading ? <Loading /> : (
        <Section title={`Messages (${messages.length})`}>
          {messages.length === 0 ? <Empty>This thread has no stored messages.</Empty> : (
            <div className="flex flex-col gap-3">
              {messages.map((m, i) => (
                <div key={i} className={`rounded-xl border px-3 py-2 flex flex-col gap-1 ${m.is_reply
                  ? 'bg-grayscale-100 border-grayscale-300' : 'bg-primary-100 border-primary-300'}`}>
                  <div className="flex items-center gap-2 flex-wrap text-[11px] text-grayscale-1100">
                    <Pill tone={m.is_reply ? 'ok' : 'mute'}>{m.message_type || (m.is_reply ? 'reply' : 'sent')}</Pill>
                    <span className="break-all">{m.from_email} → {m.to_email}</span>
                    <span>{fmtDate(m.created_at)}</span>
                  </div>
                  {m.subject && <div className="text-sm font-bold text-grayscale-1300">{m.subject}</div>}
                  <p className="text-sm text-grayscale-1300 whitespace-pre-wrap break-words">
                    {m.content_text || '—'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// 8. Sessions
// ---------------------------------------------------------------------------
const SESSION_COLS = ['when', 'ip', 'user_agent', 'active', 'expires'];

const Sessions: React.FC<TabProps> = ({ userId, range }) => {
  const rp = rangeParams(range);
  const s = useAdminGet('user360', 'sessions', { user_id: userId, limit: 500, ...rp },
    [userId, range.from, range.to]);
  const rows: any[] = s.data?.rows || [];
  const active = rows.filter(r => r.active).length;
  const ips = new Set(rows.map(r => r.ip).filter(Boolean));

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Sign-ins in range" value={num(s.data?.total ?? rows.length)} />
        <KPI label="Still active" value={num(active)} hint="loaded rows" />
        <KPI label="Distinct IPs" value={num(ips.size)} hint="loaded rows" />
      </KPIRow>
      <Section title="Sessions" right={<Btn small onClick={s.reload}>Refresh</Btn>}>
        {s.error && <Msg kind="error">{s.error}</Msg>}
        {s.data?.truncated && (
          <Note>
            Showing the newest {rows.length} of {num(s.data?.total)} sign-ins in {rangeLabel(range)}
            {' '}— narrow the date range to see further back.
          </Note>
        )}
        {s.loading ? <Loading /> : (
          <DataTable rows={rows} columns={SESSION_COLS} searchable csvName="user360_sessions"
            maxHeight="34rem"
            labels={{ when: 'Signed in', ip: 'IP', user_agent: 'User agent', expires: 'Access expires' }}
            empty={`No sign-ins in ${rangeLabel(range)}.`}
            render={{
              active: r => r.active ? <Pill tone="ok">active</Pill> : <Pill tone="mute">expired</Pill>,
              user_agent: r => <span className="block max-w-[28rem] truncate" title={r.user_agent}>{r.user_agent || '—'}</span>,
            }} />
        )}
      </Section>
    </div>
  );
};

export default User360Panel;
